import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { BookFilters } from '@/components/books/FilterPanel'

const getStorageKey = (context: string) => `bookFilters_${context}`

const defaultFilters: BookFilters = {
  search: '',
  genres: [],
  statuses: [],
  ratingRange: { min: 0, max: 5 },
  sortBy: 'created_at',
  sortOrder: 'desc',
}

interface UseFilterPersistenceOptions {
  statuses?: string[]
}

export function useFilterPersistence(
  context: string = 'default',
  options?: UseFilterPersistenceOptions
) {
  const [filters, setFilters] = useState<BookFilters>(() => ({
    ...defaultFilters,
    ...(options?.statuses && { statuses: options.statuses }),
  }))
  const [isInitialized, setIsInitialized] = useState(false)

  // One-shot overrides from the URL (e.g. links from the dashboard: ?status=read&genre=<id>&rating=5&year=2026).
  // ?status=all clears the status filter.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlOverrides = useRef<Partial<BookFilters> | null>(null)
  if (urlOverrides.current === null) {
    const status = searchParams.get('status')
    const genre = searchParams.get('genre')
    const rating = Number(searchParams.get('rating'))
    const hasRating = rating >= 1 && rating <= 5
    const year = Number(searchParams.get('year'))
    const hasYear = Number.isInteger(year) && year > 1900
    const hasAny = !!(status || genre || hasRating || hasYear)
    urlOverrides.current = {
      // A link from the dashboard describes the whole view: reset filters it doesn't mention
      ...(hasAny && { search: '', readYear: hasYear ? year : null }),
      ...(status && { statuses: status === 'all' ? [] : status.split(',') }),
      ...(genre && { genres: genre.split(',') }),
      ...(hasRating && { ratingRange: { min: rating, max: rating } }),
    }
  }

  // Load filters from localStorage on mount
  useEffect(() => {
    try {
      const storageKey = getStorageKey(context)
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsedFilters = JSON.parse(saved) as BookFilters
        // Merge with default values to handle schema changes
        setFilters({
          ...defaultFilters,
          ...parsedFilters,
          ...urlOverrides.current,
          // Override with options if provided
          ...(options?.statuses && { statuses: options.statuses }),
        })
      } else {
        // If no saved filters, apply default options
        setFilters({
          ...defaultFilters,
          ...urlOverrides.current,
          ...(options?.statuses && { statuses: options.statuses }),
        })
      }
    } catch (error) {
      console.warn('Failed to load filters from localStorage:', error)
    } finally {
      setIsInitialized(true)
    }
  }, [context, options])

  // Drop consumed URL overrides so later filter changes (and refreshes) aren't overridden again
  useEffect(() => {
    const consumed = ['status', 'genre', 'rating', 'year'].filter((key) => searchParams.has(key))
    if (consumed.length > 0) {
      const next = new URLSearchParams(searchParams)
      for (const key of consumed) next.delete(key)
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Save filters to localStorage when they change
  useEffect(() => {
    if (!isInitialized) return

    try {
      const storageKey = getStorageKey(context)
      localStorage.setItem(storageKey, JSON.stringify(filters))
    } catch (error) {
      console.warn('Failed to save filters to localStorage:', error)
    }
  }, [filters, context, isInitialized])

  const updateFilters = (newFilters: BookFilters) => {
    setFilters(newFilters)
  }

  const resetFilters = () => {
    setFilters({
      ...defaultFilters,
      ...(options?.statuses && { statuses: options.statuses }),
    })
  }

  return {
    filters,
    updateFilters,
    resetFilters,
    isInitialized,
  }
}
